import { useState } from "react";
import api from "@/lib/api";
import { DollarSign, TrendingUp, Building2, Sparkles, Lightbulb } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

export default function SalaryInsights() {
  const [form, setForm] = useState({ role: "", location: "India", years_experience: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const analyze = async () => {
    setLoading(true);
    try {
      const r = await api.post("/salary/insights", {
        role: form.role,
        location: form.location,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      });
      setData(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const chartData = data ? [
    { band: "Entry", min: data.band_entry?.min, median: data.band_entry?.median, max: data.band_entry?.max, active: data.current_band === "Entry" },
    { band: "Mid", min: data.band_mid?.min, median: data.band_mid?.median, max: data.band_mid?.max, active: data.current_band === "Mid" },
    { band: "Senior", min: data.band_senior?.min, median: data.band_senior?.median, max: data.band_senior?.max, active: ["Senior", "Lead", "Principal"].includes(data.current_band) },
  ] : [];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="salary-page">
      <div className="jr-card">
        <div className="grid md:grid-cols-4 gap-3">
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (e.g. Gen AI Engineer)" className="jr-input" data-testid="salary-role" />
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="jr-input" data-testid="salary-location" />
          <input type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} placeholder="Years experience" className="jr-input" data-testid="salary-years" />
          <button onClick={analyze} disabled={loading} className="jr-btn-primary" data-testid="analyze-salary-btn">
            <Sparkles className="w-4 h-4 inline mr-1" /> {loading ? "Analyzing..." : "Get Insights"}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Leave fields blank to use your profile defaults. Powered by Claude Sonnet 4.5.</p>
      </div>

      {loading && <div className="jr-card text-center text-violet-300 jr-pulse py-8">Claude is analyzing market data…</div>}

      {data && (
        <div className="space-y-6 jr-fade-in">
          <div className="grid md:grid-cols-3 gap-3">
            <BandCard title="Entry" band={data.band_entry} currency={data.currency} highlight={data.current_band === "Entry"} />
            <BandCard title="Mid" band={data.band_mid} currency={data.currency} highlight={data.current_band === "Mid"} />
            <BandCard title="Senior+" band={data.band_senior} currency={data.currency} highlight={["Senior", "Lead", "Principal"].includes(data.current_band)} />
          </div>

          <div className="jr-card" data-testid="salary-your-band">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-emerald-300" />
              <div className="font-semibold text-white">Your Estimated Range</div>
              <span className="jr-pill bg-emerald-500/15 text-emerald-200 text-[10px]">{data.current_band}</span>
            </div>
            <div className="text-xs text-zinc-400 mb-4">For <strong className="text-white">{data.role}</strong> in {data.location}</div>
            <div className="flex items-baseline gap-4 mb-4">
              <div className="text-4xl font-bold text-emerald-300">{fmt(data.your_estimate?.median, data.currency)}</div>
              <div className="text-sm text-zinc-400">
                {fmt(data.your_estimate?.min, data.currency)} – {fmt(data.your_estimate?.max, data.currency)}
              </div>
            </div>
            <div className="h-48">
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <XAxis dataKey="band" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => shortNum(v)} />
                  <Tooltip formatter={(v) => fmt(v, data.currency)} contentStyle={{ background: "#161635", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 8 }} />
                  <Bar dataKey="median" radius={[6, 6, 0, 0]}>
                    {chartData.map((e, i) => (
                      <Cell key={`c-${i}`} fill={e.active ? "#10b981" : "#8b5cf6"} opacity={e.active ? 1 : 0.5} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {data.top_paying_companies?.length > 0 && (
            <div className="jr-card">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-cyan-300" />
                <div className="font-semibold text-white">Top Paying Companies</div>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {data.top_paying_companies.map((c, i) => (
                  <div key={`co-${i}-${c.name}`} className="p-3 rounded-lg bg-white/[0.03] flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center text-white font-bold text-xs">
                      {c.name?.slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">{c.name}</div>
                      <div className="text-xs text-zinc-400">{c.note}</div>
                    </div>
                    <span className="jr-pill bg-emerald-500/15 text-emerald-200">{c.band}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.insights?.length > 0 && (
            <div className="jr-card">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-amber-300" />
                <div className="font-semibold text-white">Market Insights</div>
              </div>
              <ul className="space-y-2 text-sm text-zinc-200">
                {data.insights.map((s, i) => <li key={`ins-${i}`} className="pl-3 border-l-2 border-amber-500/40">{s}</li>)}
              </ul>
            </div>
          )}

          {data.negotiation_tips?.length > 0 && (
            <div className="jr-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-violet-300" />
                <div className="font-semibold text-white">Negotiation Tips</div>
              </div>
              <ul className="space-y-2 text-sm text-zinc-200">
                {data.negotiation_tips.map((s, i) => <li key={`neg-${i}`} className="pl-3 border-l-2 border-violet-500/40">{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BandCard({ title, band, currency, highlight }) {
  return (
    <div className={`jr-card ${highlight ? "border-emerald-500/50" : ""}`}>
      <div className="text-xs text-zinc-400 uppercase tracking-wider">{title} band</div>
      <div className={`text-2xl font-bold mt-1 ${highlight ? "text-emerald-300" : "text-white"}`}>{fmt(band?.median, currency)}</div>
      <div className="text-xs text-zinc-500 mt-1">{fmt(band?.min, currency)} – {fmt(band?.max, currency)}</div>
    </div>
  );
}

function shortNum(v) {
  if (!v && v !== 0) return "";
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return v.toString();
}

function fmt(v, cur) {
  if (!v && v !== 0) return "—";
  const sym = cur === "USD" ? "$" : cur === "INR" ? "₹" : "";
  return `${sym}${shortNum(v)}`;
}
